import React, { useState, useEffect } from "react";
import "./styles/DownloadReportWindow.css";
import ArrowDown from "/assets/ArrowDown.svg";
import ReactDOM from "react-dom";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import { font } from "./THSarabunNew-normal";

function DownloadReportWindow({ onClose, jsonData, fileName, routeName }) {
  const [selectedFormat, setSelectedFormat] = useState(null);
  const [formatDropdownOpen, setFormatDropdownOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    console.log("jsonData:", jsonData);
  }, [jsonData]);

  const toggleFormatDropdown = () => {
    setFormatDropdownOpen(!formatDropdownOpen);
  };

  const handleFormatSelect = (format) => {
    setSelectedFormat(format);
    setErrorMessage("");
    setFormatDropdownOpen(false);
  };

  const handleDownloadClick = async () => {
    setErrorMessage(""); // Clear any previous error message
    if (selectedFormat) {
      setLoading(true);
      setTimeout(async () => {
        try {
          if (!jsonData || typeof jsonData !== 'object') {
            throw new Error("Invalid JSON data");
          }
          if (selectedFormat === ".xlsx") {
            await generateAndDownloadExcel();
          } else if (selectedFormat === ".pdf") {
            generateAndDownloadPDF();
          } else if (selectedFormat === ".csv") {
            generateAndDownloadCSV();
          }
        } catch (error) {
          console.error("Error during file generation or download:", error);
          setErrorMessage("เกิดข้อผิดพลาดในการสร้างไฟล์: " + error.message);
        } finally {
          setLoading(false);
          if (!errorMessage) {
            onClose();
          }
        }
      }, 1000);
    } else {
      console.error("Please select a file format before downloading.");
      setErrorMessage("กรุณาเลือกรูปแบบไฟล์ก่อนดาวน์โหลด");
    }
  };

  const generateExcelByRoute = (routeName, worksheet, jsonData) => {
    switch (routeName) {
      case "showweather":
        generateWeatherReport(worksheet, jsonData);
        break;
      case "showwater":
        generateWaterReport(worksheet, jsonData);
        break;
      case "showwaterlevel":
        generateWaterLevelStatus24HoursReport(worksheet, jsonData);
        break;
      case "showpoleenergy":
        generatePoleEnergyReport(worksheet, jsonData);
        break;
      case "showpoleenergy24":
        generatePoleEnergy24Report(worksheet, jsonData);
        break;
      case "showdetailedreport":
        generateDetailedReport(worksheet, jsonData);
        break;
      case "showuserreport":
        generateUserReport(worksheet, jsonData);
        break;
      case "showbranches":
        generateBranchesReport(worksheet, jsonData);
        break;
      case "showbranchdetail":
        generateBranchDetailReport(worksheet, jsonData);
        break;
      default:
        console.error("Unknown route for report generation");
    }
  };

  const generateAndDownloadExcel = async () => {
    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Report");

      generateExcelByRoute(routeName, worksheet, jsonData);

      // Apply styles to header rows
      worksheet.eachRow((row, rowNumber) => {
        if (rowNumber === 1) {
          row.eachCell((cell) => {
            cell.font = { bold: true, color: { argb: "000000" } };
            cell.fill = {
              type: "pattern",
              pattern: "solid",
              fgColor: { argb: "FFFFFF" },
            };
          });
        }
      });

      // Save the file
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: "application/octet-stream" });
      saveAs(blob, `${fileName}.xlsx`);
    } catch (error) {
      console.error("Error generating or downloading Excel file:", error);
    }
  };

  const generatePDFByRoute = (routeName, doc) => {
    switch (routeName) {
      case "showweather":
        generateWeatherReportPDF(doc, jsonData);
        break;
      case "showwater":
        generateWaterReportPDF(doc, jsonData);
        break;
      case "showwaterlevel":
        generateWaterLevelStatus24HoursReportPDF(doc, jsonData);
        break;
      case "showpoleenergy":
        generatePoleEnergyReportPDF(doc, jsonData);
        break;
      case "showpoleenergy24":
        generatePoleEnergy24ReportPDF(doc, jsonData);
        break;
      case "showdetailedreport":
        generateDetailedReportPDF(doc, jsonData);
        break;
      case "showuserreport":
        generateUserReportPDF(doc, jsonData);
        break;
      case "showbranches":
        generateBranchesReportPDF(doc, jsonData);
        break;
      case "showbranchdetail":
        generateBranchDetailReportPDF(doc, jsonData);
        break;
      default:
        console.error("Unknown route for report generation");
    }
  };

  const generateAndDownloadPDF = () => {
    try {
      // Create a new jsPDF instance with landscape orientation
      const doc = new jsPDF("landscape");

      // Add the font to jsPDF
      doc.addFileToVFS("THSarabunNew.ttf", font);
      doc.addFont("THSarabunNew.ttf", "THSarabunNew", "normal");
      doc.setFont("THSarabunNew");

      generatePDFByRoute(routeName, doc);
      doc.save(`${fileName}.pdf`);
    } catch (error) {
      console.error("Error generating or downloading PDF file:", error);
    }
  };

  const generateWeatherReport = (worksheet, data) => {
    const { poleInfo, reportInfo, sensorData } = data;

    worksheet.addRow(["รายงานสภาพอากาศ"]);
    worksheet.addRow(["ชื่อเสา", poleInfo.name]);
    worksheet.addRow(["ที่ตั้ง", poleInfo.location]);
    worksheet.addRow(["ประเภทเสา", poleInfo.poleType]);
    worksheet.addRow(["Latitude", poleInfo.latitude]);
    worksheet.addRow(["Longitude", poleInfo.longitude]);
    worksheet.addRow(["ID เสา", poleInfo.id]);
    worksheet.addRow([]);
    worksheet.addRow(["ช่วงเวลารายงาน"]);
    worksheet.addRow(["ระหว่างวันที่", reportInfo.startDate]);
    worksheet.addRow(["ถึงวันที่", reportInfo.endDate]);
    worksheet.addRow([]);

    const headers = [
      "Timestamp",
      "Temperature",
      "Humidity",
      "Rain",
      "Daily Rain",
      "Wind Speed",
      "Wind Direction",
      "Wind Gust Speed",
      "Wind Gust Direction",
      "Pressure",
      "PM1",
      "PM2.5",
      "PM10",
    ];
    createHeaderRow(worksheet, headers);

    worksheet.columns = [
      { key: "timestamp", width: 20 },
      { key: "temperature", width: 15 },
      { key: "humidity", width: 15 },
      { key: "rain", width: 10 },
      { key: "dailyRain", width: 15 },
      { key: "windSpeed", width: 15 },
      { key: "windDirection", width: 15 },
      { key: "windGustSpeed", width: 15 },
      { key: "windGustDirection", width: 17 },
      { key: "pressure", width: 15 },
      { key: "pm1", width: 10 },
      { key: "pm2_5", width: 10 },
      { key: "pm10", width: 10 },
    ];

    sensorData.forEach((item) => {
      const dataRow = worksheet.addRow([
        item.timestamp,
        item.temperature,
        item.humidity,
        item.rain,
        item.rainDaily,
        item.windSpeed,
        item.windDirection,
        item.windGustSpeed,
        item.windGustDirection,
        item.pressure,
        item.pm1,
        item.pm2_5,
        item.pm10,
      ]);
      dataRow.eachCell((cell) => {
        cell.alignment = { vertical: "middle", horizontal: "right" };
      });
    });
  };

  const createHeaderRow = (worksheet, headers) => {
    const headerRow = worksheet.addRow(headers);
    headerRow.eachCell((cell) => {
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "D0D5DD" },
      };
      cell.border = {
        top: { style: "thin", color: { argb: "000000" } },
        left: { style: "thin", color: { argb: "000000" } },
        bottom: { style: "thin", color: { argb: "000000" } },
        right: { style: "thin", color: { argb: "000000" } },
      };
      cell.alignment = { vertical: "middle" };
    });
    return headerRow;
  };

  const generateWeatherReportPDF = (doc, data) => {
    const { poleInfo, reportInfo, sensorData } = data;

    // Header
    doc.autoTable({
      head: [["รายงานสภาพอากาศ"]],
      body: [],
      styles: {
        font: "THSarabunNew",
        fontSize: 16,
        halign: "center",
        valign: "middle",
        fillColor: [131, 174, 240],
        textColor: [0, 0, 0],
        fontStyle: "bold",
      },
      startY: 20,
      tableWidth: "auto",
    });

    // Combined Pole and Report Info Table
    const combinedInfoHeaders = [
      "ชื่อเสา",
      "ที่ตั้ง",
      "ประเภทเสา",
      "Latitude",
      "Longitude",
      "ID เสา",
      "ช่วงเวลารายงาน",
    ];
    const combinedInfoRow = [
      poleInfo.name,
      poleInfo.location,
      poleInfo.poleType,
      poleInfo.latitude,
      poleInfo.longitude,
      poleInfo.id,
      `${reportInfo.startDate} - ${reportInfo.endDate}`,
    ];

    doc.autoTable({
      head: [combinedInfoHeaders],
      body: [combinedInfoRow],
      startY: doc.autoTable.previous.finalY,
      styles: {
        font: "THSarabunNew",
        fontSize: 12,
        halign: "center",
        valign: "middle",
        textColor: [0, 0, 0],
      },
      headStyles: {
        fillColor: [221, 230, 254],
        textColor: [0, 0, 0],
        fontStyle: "bold",
      },
      columnStyles: {
        6: { cellWidth: "auto" },
      },
    });

    // Sensor Data Table
    const headers = [
      "Time Stamp",
      "Temperature",
      "Humidity",
      "Rain",
      "Daily Rain",
      "Wind Speed",
      "Wind Direction",
      "Wind Gust Speed",
      "Wind Gust Direction",
      "Pressure",
      "PM1",
      "PM2.5",
      "PM10",
    ];

    const rows = sensorData.map((item) => [
      item.timestamp,
      item.temperature,
      item.humidity,
      item.rain,
      item.rainDaily,
      item.windSpeed,
      item.windDirection,
      item.windGustSpeed,
      item.windGustDirection,
      item.pressure,
      item.pm1,
      item.pm2_5,
      item.pm10,
    ]);

    doc.autoTable({
      head: [headers],
      body: rows,
      startY: doc.autoTable.previous.finalY,
      styles: {
        font: "THSarabunNew",
        fontSize: 10,
        halign: "center",
        valign: "middle",
        textColor: [0, 0, 0],
        cellPadding: 2,
      },
      headStyles: {
        fillColor: [221, 230, 254],
        textColor: [0, 0, 0],
        fontStyle: "bold",
      },
      columnStyles: {
        0: { cellWidth: "auto" },
      },
      alternateRowStyles: {
        fillColor: [248, 248, 248],
      },
    });
  };

  const generateWaterReport = (worksheet, data) => {
    const { poleInfo, reportInfo, sensorData } = data;

    worksheet.addRow(["รายงานสถานะระดับน้ำรายชั่วโมง"]);
    worksheet.addRow(["ชื่อเสา", poleInfo.name]);
    worksheet.addRow(["สถานที่", poleInfo.location]);
    worksheet.addRow(["ประเภทเสา", poleInfo.poleType]);
    worksheet.addRow(["Latitude", poleInfo.latitude]);
    worksheet.addRow(["Longitude", poleInfo.longitude]);
    worksheet.addRow(["ID เสา", poleInfo.id]);
    worksheet.addRow([]);
    worksheet.addRow(["ช่วงเวลารายงาน"]);
    worksheet.addRow(["ระหว่างวันที่", reportInfo.startDate]);
    worksheet.addRow(["ถึงวันที่", reportInfo.endDate]);
    worksheet.addRow([]);

    const headers = [
      "วันที่และเวลา",
      "ปริมาณน้ำฝน (นิ้ว)",
      "ปริมาณน้ำฝนเฉลี่ยทั้งวัน (นิ้ว)",
      "สถานะน้ำที่โคนเสา",
    ];
    createHeaderRow(worksheet, headers);

    worksheet.columns = [
      { key: "timestamp", width: 25 },
      { key: "rainin", width: 25 },
      { key: "dailyrainin", width: 25 },
      { key: "waterStatus", width: 25 },
    ];

    sensorData.forEach((item) => {
      const dataRow = worksheet.addRow([
        item.timestamp,
        item.rainin,
        item.dailyrainin,
        item.waterStatus,
      ]);
      dataRow.eachCell((cell) => {
        cell.alignment = { vertical: "middle", horizontal: "right" };
      });
    });
  };

  const generateWaterReportPDF = (doc, data) => {
    const { poleInfo, reportInfo, sensorData } = data;

    // Header
    doc.autoTable({
      head: [["รายงานสถานะระดับน้ำรายชั่วโมง"]],
      body: [],
      styles: {
        font: "THSarabunNew",
        fontSize: 16,
        halign: "center",
        valign: "middle",
        fillColor: [131, 174, 240],
        textColor: [0, 0, 0],
        fontStyle: "bold",
      },
      startY: 20,
      tableWidth: "auto",
    });

    // Combined Pole and Report Info Table
    const combinedInfoHeaders = [
      "ชื่อเสา",
      "ที่ตั้ง",
      "ประเภทเสา",
      "Latitude",
      "Longitude",
      "ID เสา",
      "ช่วงเวลารายงาน",
    ];
    const combinedInfoRow = [
      poleInfo.name,
      poleInfo.location,
      poleInfo.poleType,
      poleInfo.latitude,
      poleInfo.longitude,
      poleInfo.id,
      `${reportInfo.startDate} - ${reportInfo.endDate}`,
    ];

    doc.autoTable({
      head: [combinedInfoHeaders],
      body: [combinedInfoRow],
      startY: doc.autoTable.previous.finalY,
      styles: {
        font: "THSarabunNew",
        fontSize: 12,
        halign: "center",
        valign: "middle",
        textColor: [0, 0, 0],
      },
      headStyles: {
        fillColor: [221, 230, 254],
        textColor: [0, 0, 0],
        fontStyle: "bold",
      },
      columnStyles: {
        6: { cellWidth: "auto" },
      },
    });

    // Sensor Data Table
    const headers = [
      "วันที่และเวลา",
      "ปริมาณน้ำฝน (นิ้ว)",
      "ปริมาณน้ำฝนเฉลี่ยทั้งวัน (นิ้ว)",
      "สถานะน้ำที่โคนเสา",
    ];

    const rows = sensorData.map((item) => [
      item.timestamp,
      item.rainin,
      item.dailyrainin,
      item.waterStatus,
    ]);

    doc.autoTable({
      head: [headers],
      body: rows,
      startY: doc.autoTable.previous.finalY,
      styles: {
        font: "THSarabunNew",
        fontSize: 10,
        halign: "center",
        valign: "middle",
        textColor: [0, 0, 0],
        cellPadding: 2,
      },
      headStyles: {
        fillColor: [221, 230, 254],
        textColor: [0, 0, 0],
        fontStyle: "bold",
      },
      columnStyles: {
        0: { cellWidth: "auto" },
      },
      alternateRowStyles: {
        fillColor: [248, 248, 248],
      },
    });
  };

  const generateWaterLevelStatus24HoursReport = (worksheet, data) => {
    const { poleInfo, reportInfo, averageData } = data;

    worksheet.addRow(["รายงานสถานะระดับน้ำ 24 ชั่วโมง"]);
    worksheet.addRow(["ชื่อเสา", poleInfo.name]);
    worksheet.addRow(["สถานที่", poleInfo.location]);
    worksheet.addRow(["ประเภทเสา", poleInfo.poleType]);
    worksheet.addRow(["Latitude", poleInfo.latitude]);
    worksheet.addRow(["Longitude", poleInfo.longitude]);
    worksheet.addRow(["ID เสา", poleInfo.id]);
    worksheet.addRow([]);
    worksheet.addRow(["ช่วงเวลารายงาน"]);
    worksheet.addRow(["ระหว่างวันที่", reportInfo.startDate]);
    worksheet.addRow(["ถึงวันที่", reportInfo.endDate]);
    worksheet.addRow([]);

    const headers = [
      "ปริมาณน้ำฝนเฉลี่ย (นิ้ว)",
      "ปริมาณน้ำฝนรายวันเฉลี่ย (นิ้ว)",
      "สถานะน้ำที่โคนเสา",
    ];
    createHeaderRow(worksheet, headers);

    worksheet.columns = [
      { key: "avgRainin", width: 30 },
      { key: "avgDailyrainin", width: 30 },
      { key: "floodStatus", width: 30 },
    ];

    worksheet.addRow([
      averageData.avgRainin,
      averageData.avgDailyrainin,
      averageData.floodStatus,
    ]);
  };

  const generateWaterLevelStatus24HoursReportPDF = (doc, data) => {
    const { poleInfo, reportInfo, averageData } = data;

    // Header
    doc.autoTable({
      head: [["รายงานสถานะระดับน้ำ 24 ชั่วโมง"]],
      body: [],
      styles: {
        font: "THSarabunNew",
        fontSize: 16,
        halign: "center",
        valign: "middle",
        fillColor: [131, 174, 240],
        textColor: [0, 0, 0],
        fontStyle: "bold",
      },
      startY: 20,
      tableWidth: "auto",
    });

    // Combined Pole and Report Info Table
    const combinedInfoHeaders = [
      "ชื่อเสา",
      "ที่ตั้ง",
      "ประเภทเสา",
      "Latitude",
      "Longitude",
      "ID เสา",
      "ช่วงเวลารายงาน",
    ];
    const combinedInfoRow = [
      poleInfo.name,
      poleInfo.location,
      poleInfo.poleType,
      poleInfo.latitude,
      poleInfo.longitude,
      poleInfo.id,
      `${reportInfo.startDate} - ${reportInfo.endDate}`,
    ];

    doc.autoTable({
      head: [combinedInfoHeaders],
      body: [combinedInfoRow],
      startY: doc.autoTable.previous.finalY,
      styles: {
        font: "THSarabunNew",
        fontSize: 12,
        halign: "center",
        valign: "middle",
        textColor: [0, 0, 0],
      },
      headStyles: {
        fillColor: [221, 230, 254],
        textColor: [0, 0, 0],
        fontStyle: "bold",
      },
      columnStyles: {
        6: { cellWidth: "auto" },
      },
    });

    // Sensor Data Table
    const headers = [
      "ปริมาณน้ำฝนเฉลี่ย (นิ้ว)",
      "ปริมาณน้ำฝนรายวันเฉลี่ย (นิ้ว)",
      "สถานะน้ำที่โคนเสา",
    ];

    const rows = [
      [
        averageData.avgRainin,
        averageData.avgDailyrainin,
        averageData.floodStatus,
      ],
    ];

    doc.autoTable({
      head: [headers],
      body: rows,
      startY: doc.autoTable.previous.finalY,
      styles: {
        font: "THSarabunNew",
        fontSize: 10,
        halign: "center",
        valign: "middle",
        textColor: [0, 0, 0],
        cellPadding: 2,
      },
      headStyles: {
        fillColor: [221, 230, 254],
        textColor: [0, 0, 0],
        fontStyle: "bold",
      },
      columnStyles: {
        0: { cellWidth: "auto" },
      },
      alternateRowStyles: {
        fillColor: [248, 248, 248],
      },
    });
  };

  const generatePoleEnergyReport = (worksheet, data) => {
    const { poleInfo, reportInfo, sensorData } = data;

    worksheet.addRow(["รายงานสถานะการใช้พลังงานเสา"]);
    worksheet.addRow(["ชื่อเสา", poleInfo.name]);
    worksheet.addRow(["ที่ตั้ง", poleInfo.location]);
    worksheet.addRow(["ประเภทเสา", poleInfo.poleType]);
    worksheet.addRow(["Latitude", poleInfo.latitude]);
    worksheet.addRow(["Longitude", poleInfo.longitude]);
    worksheet.addRow(["ID เสา", poleInfo.id]);
    worksheet.addRow([]);
    worksheet.addRow(["ช่วงเวลารายงาน"]);
    worksheet.addRow(["ระหว่างวันที่", reportInfo.startDate]);
    worksheet.addRow(["ถึงวันที่", reportInfo.endDate]);
    worksheet.addRow([]);

    const headers = [
      "Timestamp",
      "แรงดันไฟฟ้า (Volt)",
      "กระแสไฟฟ้า (Amp)",
      "กำลังไฟฟ้า (Watt)",
      "สถานะ",
    ];
    createHeaderRow(worksheet, headers);

    worksheet.columns = [
      { key: "timestamp", width: 20 },
      { key: "volt", width: 20 },
      { key: "amp", width: 20 },
      { key: "watt", width: 20 },
      { key: "energy", width: 20 },
    ];

    sensorData.forEach((item) => {
      const dataRow = worksheet.addRow([
        item.timestamp,
        item.volt,
        item.amp,
        item.watt,
        item.energy,
      ]);
      dataRow.eachCell((cell) => {
        cell.alignment = { vertical: "middle", horizontal: "right" };
      });
    });
  };

  const generatePoleEnergyReportPDF = (doc, data) => {
    const { poleInfo, reportInfo, sensorData } = data;

    // Header
    doc.autoTable({
      head: [["รายงานสถานะการใช้พลังงานเสา"]],
      body: [],
      styles: {
        font: "THSarabunNew",
        fontSize: 16,
        halign: "center",
        valign: "middle",
        fillColor: [131, 174, 240],
        textColor: [0, 0, 0],
        fontStyle: "bold",
      },
      startY: 20,
      tableWidth: "auto",
    });

    // Combined Pole and Report Info Table
    const combinedInfoHeaders = [
      "ชื่อเสา",
      "ที่ตั้ง",
      "ประเภทเสา",
      "Latitude",
      "Longitude",
      "ID เสา",
      "ช่วงเวลารายงาน",
    ];
    const combinedInfoRow = [
      poleInfo.name,
      poleInfo.location,
      poleInfo.poleType,
      poleInfo.latitude,
      poleInfo.longitude,
      poleInfo.id,
      `${reportInfo.startDate} - ${reportInfo.endDate}`,
    ];

    doc.autoTable({
      head: [combinedInfoHeaders],
      body: [combinedInfoRow],
      startY: doc.autoTable.previous.finalY,
      styles: {
        font: "THSarabunNew",
        fontSize: 12,
        halign: "center",
        valign: "middle",
        textColor: [0, 0, 0],
      },
      headStyles: {
        fillColor: [221, 230, 254],
        textColor: [0, 0, 0],
        fontStyle: "bold",
      },
      columnStyles: {
        6: { cellWidth: "auto" },
      },
    });

    // Sensor Data Table
    const headers = [
      "Timestamp",
      "แรงดันไฟฟ้า (Volt)",
      "กระแสไฟฟ้า (Amp)",
      "กำลังไฟฟ้า (Watt)",
      "สถานะ",
    ];

    const rows = sensorData.map((item) => [
      item.timestamp,
      item.volt,
      item.amp,
      item.watt,
      item.energy,
    ]);

    doc.autoTable({
      head: [headers],
      body: rows,
      startY: doc.autoTable.previous.finalY,
      styles: {
        font: "THSarabunNew",
        fontSize: 10,
        halign: "center",
        valign: "middle",
        textColor: [0, 0, 0],
        cellPadding: 2,
      },
      headStyles: {
        fillColor: [221, 230, 254],
        textColor: [0, 0, 0],
        fontStyle: "bold",
      },
      columnStyles: {
        0: { cellWidth: "auto" },
      },
      alternateRowStyles: {
        fillColor: [248, 248, 248],
      },
    });
  };

  const generatePoleEnergy24Report = (worksheet, data) => {
    const { poleInfo, reportInfo, sensorData } = data;

    worksheet.addRow(["รายงานสถานะการใช้พลังงานเสา 24 ชั่วโมง"]);
    worksheet.addRow(["ชื่อเสา", poleInfo.name]);
    worksheet.addRow(["ที่ตั้ง", poleInfo.location]);
    worksheet.addRow(["ประเภทเสา", poleInfo.poleType]);
    worksheet.addRow(["Latitude", poleInfo.latitude]);
    worksheet.addRow(["Longitude", poleInfo.longitude]);
    worksheet.addRow(["ID เสา", poleInfo.id]);
    worksheet.addRow([]);
    worksheet.addRow(["ช่วงเวลารายงาน"]);
    worksheet.addRow(["ระหว่างวันที่", reportInfo.startDate]);
    worksheet.addRow(["ถึงวันที่", reportInfo.endDate]);
    worksheet.addRow([]);

    const headers = [
      "แรงดันไฟฟ้าเฉลี่ย (V)",
      "กระแสไฟฟ้าเฉลี่ย (A)",
      "กำลังไฟฟ้าเฉลี่ย (W)",
    ];
    createHeaderRow(worksheet, headers);

    worksheet.columns = [
      { key: "avg_voltage", width: 20 },
      { key: "avg_current", width: 20 },
      { key: "avg_power", width: 20 },
    ];

    sensorData.forEach((item) => {
      const dataRow = worksheet.addRow([
        item.voltage,
        item.current,
        item.power,
      ]);
      dataRow.eachCell((cell) => {
        cell.alignment = { vertical: "middle", horizontal: "right" };
      });
    });
  };

  const generatePoleEnergy24ReportPDF = (doc, data) => {
    const { poleInfo, reportInfo, sensorData } = data;

    // Header
    doc.autoTable({
      head: [["รายงานสถานะการใช้พลังงานเสา 24 ชั่วโมง"]],
      body: [],
      styles: {
        font: "THSarabunNew",
        fontSize: 16,
        halign: "center",
        valign: "middle",
        fillColor: [131, 174, 240],
        textColor: [0, 0, 0],
        fontStyle: "bold",
      },
      startY: 20,
      tableWidth: "auto",
    });

    // Combined Pole and Report Info Table
    const combinedInfoHeaders = [
      "ชื่อเสา",
      "ที่ตั้ง",
      "ประเภทเสา",
      "Latitude",
      "Longitude",
      "ID เสา",
      "ช่วงเวลารายงาน",
    ];
    const combinedInfoRow = [
      poleInfo.name,
      poleInfo.location,
      poleInfo.poleType,
      poleInfo.latitude,
      poleInfo.longitude,
      poleInfo.id,
      `${reportInfo.startDate} - ${reportInfo.endDate}`,
    ];

    doc.autoTable({
      head: [combinedInfoHeaders],
      body: [combinedInfoRow],
      startY: doc.autoTable.previous.finalY,
      styles: {
        font: "THSarabunNew",
        fontSize: 12,
        halign: "center",
        valign: "middle",
        textColor: [0, 0, 0],
      },
      headStyles: {
        fillColor: [221, 230, 254],
        textColor: [0, 0, 0],
        fontStyle: "bold",
      },
      columnStyles: {
        6: { cellWidth: "auto" },
      },
    });

    // Sensor Data Table
    const headers = [
      "แรงดันไฟฟ้าเฉลี่ย (V)",
      "กระแสไฟฟ้าเฉลี่ย (A)",
      "กำลังไฟฟ้าเฉลี่ย (W)",
    ];

    const rows = sensorData.map((item) => [
      item.voltage,
      item.current,
      item.power,
    ]);

    doc.autoTable({
      head: [headers],
      body: rows,
      startY: doc.autoTable.previous.finalY,
      styles: {
        font: "THSarabunNew",
        fontSize: 10,
        halign: "center",
        valign: "middle",
        textColor: [0, 0, 0],
        cellPadding: 2,
      },
      headStyles: {
        fillColor: [221, 230, 254],
        textColor: [0, 0, 0],
        fontStyle: "bold",
      },
      columnStyles: {
        0: { cellWidth: "auto" },
      },
      alternateRowStyles: {
        fillColor: [248, 248, 248],
      },
    });
  };

  const generateDetailedReport = (worksheet, data) => {
    const { poleInfo, sensorData, schedules } = data;

    worksheet.addRow(["รายงานรายละเอียดของเสา"]);
    worksheet.addRow(["ชื่อเสา", poleInfo.name]);
    worksheet.addRow(["สถานที่", poleInfo.location]);
    worksheet.addRow(["ประเภทเสา", poleInfo.poleType]);
    worksheet.addRow(["Latitude", poleInfo.latitude]);
    worksheet.addRow(["Longitude", poleInfo.longitude]);
    worksheet.addRow(["ID เสา", poleInfo.id]);
    worksheet.addRow([]);
    worksheet.addRow(["อุปกรณ์ที่ติดตั้ง"]);

    const deviceHeaders = [
      "ประเภทอุปกรณ์",
      "ชื่ออุปกรณ์",
      "รุ่นอุปกรณ์",
      "วันที่ติดตั้ง",
      "สถานะ",
    ];
    createHeaderRow(worksheet, deviceHeaders);

    worksheet.columns = [
      { key: "deviceType", width: 20 },
      { key: "name", width: 20 },
      { key: "model", width: 20 },
      { key: "installationDate", width: 20 },
      { key: "status", width: 15 },
    ];

    sensorData.forEach((device) => {
      const dataRow = worksheet.addRow([
        device.deviceType,
        device.name,
        device.model,
        device.installationDate,
        device.status,
      ]);
      dataRow.eachCell((cell) => {
        cell.alignment = { vertical: "middle", horizontal: "left" };
      });
    });
    worksheet.addRow([]);

    worksheet.addRow(["การปรับไฟฟ้าอัตโนมัติ"]);
    if (schedules && schedules.length > 0) {
      const scheduleHeaders = ["ช่วงเวลา", ...schedules.map((s) => s.period)];
      createHeaderRow(worksheet, scheduleHeaders);

      const brightnessHeaders = [
        "ความสว่าง",
        ...schedules.map((s) => `${s.brightness}%`),
      ];
      createHeaderRow(worksheet, brightnessHeaders);
    } else {
      worksheet.addRow(["ไม่มีข้อมูลตารางเวลาสำหรับเสานี้"]);
    }
  };

  const generateDetailedReportPDF = (doc, data) => {
    const { poleInfo, sensorData, schedules } = data;

    // Header
    doc.autoTable({
      head: [["รายงานรายละเอียดของเสา"]],
      body: [],
      styles: {
        font: "THSarabunNew",
        fontSize: 16,
        halign: "center",
        valign: "middle",
        fillColor: [131, 174, 240],
        textColor: [0, 0, 0],
        fontStyle: "bold",
      },
      startY: 20,
      tableWidth: "auto",
    });

    // Combined Pole and Report Info Table
    const combinedInfoHeaders = [
      "ชื่อเสา",
      "ที่ตั้ง",
      "ประเภทเสา",
      "Latitude",
      "Longitude",
      "ID เสา",
    ];
    const combinedInfoRow = [
      poleInfo.name,
      poleInfo.location,
      poleInfo.poleType,
      poleInfo.latitude,
      poleInfo.longitude,
      poleInfo.id,
    ];

    doc.autoTable({
      head: [combinedInfoHeaders],
      body: [combinedInfoRow],
      startY: doc.autoTable.previous.finalY,
      styles: {
        font: "THSarabunNew",
        fontSize: 12,
        halign: "center",
        valign: "middle",
        textColor: [0, 0, 0],
      },
      headStyles: {
        fillColor: [221, 230, 254],
        textColor: [0, 0, 0],
        fontStyle: "bold",
      },
      columnStyles: {
        6: { cellWidth: "auto" },
      },
    });

    // Sensor Data Table
    const headers = [
      "ประเภทอุปกรณ์",
      "ชื่ออุปกรณ์",
      "รุ่นอุปกรณ์",
      "วันที่ติดตั้ง",
      "สถานะ",
    ];

    const rows = sensorData.map((device) => [
      device.deviceType,
      device.name,
      device.model,
      device.installationDate,
      device.status,
    ]);

    doc.autoTable({
      head: [headers],
      body: rows,
      startY: doc.autoTable.previous.finalY,
      styles: {
        font: "THSarabunNew",
        fontSize: 10,
        halign: "center",
        valign: "middle",
        textColor: [0, 0, 0],
        cellPadding: 2,
      },
      headStyles: {
        fillColor: [221, 230, 254],
        textColor: [0, 0, 0],
        fontStyle: "bold",
      },
      columnStyles: {
        0: { cellWidth: "auto" },
      },
      alternateRowStyles: {
        fillColor: [248, 248, 248],
      },
    });

    // Schedule Data Table
    if (schedules && schedules.length > 0) {
      const scheduleHeaders = ["ช่วงเวลา", ...schedules.map((s) => s.period)];
      const brightnessHeaders = [
        "ความสว่าง",
        ...schedules.map((s) => `${s.brightness}%`),
      ];

      const allHeaders = [scheduleHeaders, brightnessHeaders];

      doc.autoTable({
        head: allHeaders,
        body: [],
        startY: doc.autoTable.previous.finalY + 10,
        styles: {
          font: "THSarabunNew",
          fontSize: 10,
          halign: "center",
          valign: "middle",
          fillColor: [221, 230, 254],
          textColor: [0, 0, 0],
          cellPadding: 2,
        },
        headStyles: {
          fillColor: [221, 230, 254],
          textColor: [0, 0, 0],
          fontStyle: "bold",
        },
        columnStyles: {
          0: { cellWidth: "auto" },
        },
        alternateRowStyles: {
          fillColor: [248, 248, 248],
        },
      });
    } else {
      doc.text(
        "ไม่มีข้อมูลตารางเวลาสำหรับเสานี้",
        20,
        doc.autoTable.previous.finalY + 10
      );
    }
  };

  const generateUserReport = (worksheet, data) => {
    const { branchInfo, userData } = data;

    worksheet.addRow(["User Report"]);
    if (branchInfo) {
      worksheet.addRow(["Branch Name", branchInfo.name]);
      worksheet.addRow(["Location", branchInfo.location]);
      worksheet.addRow(["Latitude", branchInfo.latitude]);
      worksheet.addRow(["Longitude", branchInfo.longitude]);
      worksheet.addRow(["Branch ID", branchInfo.id]);
    }
    worksheet.addRow([]);

    const headers = ["Name", "Phone Number", "Email", "Status", "Permissions"];
    createHeaderRow(worksheet, headers);

    worksheet.columns = [
      { key: "name", width: 20 },
      { key: "phone", width: 15 },
      { key: "email", width: 25 },
      { key: "status", width: 10 },
      { key: "permissions", width: 70 },
    ];

    userData.forEach((user) => {
      const dataRow = worksheet.addRow([
        user.name,
        user.phone,
        user.email,
        user.status,
        user.permissions,
      ]);
      dataRow.eachCell((cell) => {
        cell.alignment = { vertical: "middle", horizontal: "left" };
      });
    });
  };

  const generateUserReportPDF = (doc, data) => {
    const { branchInfo, userData } = data;

    // Header
    doc.autoTable({
      head: [["User Report"]],
      body: [],
      styles: {
        font: "THSarabunNew",
        fontSize: 16,
        halign: "center",
        valign: "middle",
        fillColor: [131, 174, 240],
        textColor: [0, 0, 0],
        fontStyle: "bold",
      },
      startY: 20,
      tableWidth: "auto",
    });

    // Branch Info Table
    if (branchInfo) {
      const branchInfoHeaders = [
        "Branch Name",
        "Location",
        "Latitude",
        "Longitude",
        "Branch ID",
      ];
      const branchInfoRow = [
        branchInfo.name,
        branchInfo.location,
        branchInfo.latitude,
        branchInfo.longitude,
        branchInfo.id,
      ];

      doc.autoTable({
        head: [branchInfoHeaders],
        body: [branchInfoRow],
        startY: doc.autoTable.previous.finalY,
        styles: {
          font: "THSarabunNew",
          fontSize: 12,
          halign: "center",
          valign: "middle",
          textColor: [0, 0, 0],
        },
        headStyles: {
          fillColor: [221, 230, 254],
          textColor: [0, 0, 0],
          fontStyle: "bold",
        },
      });
    }

    // User Data Table
    const headers = ["Name", "Phone Number", "Email", "Status", "Permissions"];
    const rows = userData.map((user) => [
      user.name,
      user.phone,
      user.email,
      user.status,
      user.permissions,
    ]);

    doc.autoTable({
      head: [headers],
      body: rows,
      startY: doc.autoTable.previous.finalY,
      styles: {
        font: "THSarabunNew",
        fontSize: 10,
        halign: "center",
        valign: "middle",
        textColor: [0, 0, 0],
        cellPadding: 2,
      },
      headStyles: {
        fillColor: [221, 230, 254],
        textColor: [0, 0, 0],
        fontStyle: "bold",
      },
      columnStyles: {
        0: { cellWidth: "auto" },
      },
      alternateRowStyles: {
        fillColor: [248, 248, 248],
      },
    });
  };

  const generateBranchesReport = (worksheet, data) => {
    const { branchInfo, branchDetails } = data;

    worksheet.addRow(["Branch Report"]);
    if (branchInfo) {
      worksheet.addRow(["Branch Code", branchInfo.code]);
      worksheet.addRow(["Branch Name", branchInfo.name]);
      worksheet.addRow(["Location", branchInfo.location]);
      worksheet.addRow(["Latitude", branchInfo.latitude]);
      worksheet.addRow(["Longitude", branchInfo.longitude]);
    }
    worksheet.addRow([]);

    const headers = [
      "Branch Code",
      "Branch Name",
      "Status",
      "Users Count",
      "Main Poles Count",
      "Secondary Poles Count",
    ];
    createHeaderRow(worksheet, headers);

    worksheet.columns = [
      { key: "code", width: 20 },
      { key: "name", width: 20 },
      { key: "status", width: 15 },
      { key: "usersCount", width: 15 },
      { key: "mainPolesCount", width: 20 },
      { key: "secondaryPolesCount", width: 20 },
    ];

    const dataRow = worksheet.addRow([
      branchInfo.code,
      branchInfo.name,
      branchDetails.branchStatus ? "เปิดใช้งาน" : "ปิดใช้งาน",
      branchDetails.usersCount,
      branchDetails.mainPolesCount,
      branchDetails.secondaryPolesCount,
    ]);
    dataRow.eachCell((cell) => {
      cell.alignment = { vertical: "middle", horizontal: "left" };
    });

    // กำหนดสีให้กับคำว่า "เปิดใช้งาน" หรือ "ปิดใช้งาน"
    const statusCell = dataRow.getCell(3); // เซลล์ที่ 3 คือคอลัมน์สถานะ
    statusCell.value = {
      richText: [
        {
          text: branchDetails.branchStatus ? "เปิดใช้งาน" : "ปิดใช้งาน",
          font: {
            color: {
              argb: branchDetails.branchStatus ? "FF008000" : "FFFF0000",
            },
          },
        },
      ],
    };
  };

  const generateBranchesReportPDF = (doc, data) => {
    const { branchInfo, branchDetails } = data;

    // Header
    doc.autoTable({
      head: [["Branch Report"]],
      body: [],
      styles: {
        font: "THSarabunNew",
        fontSize: 16,
        halign: "center",
        valign: "middle",
        fillColor: [131, 174, 240],
        textColor: [0, 0, 0],
        fontStyle: "bold",
      },
      startY: 20,
      tableWidth: "auto",
    });

    // Branch Info Table
    if (branchInfo) {
      const branchInfoHeaders = [
        "Branch Code",
        "Branch Name",
        "Location",
        "Latitude",
        "Longitude",
      ];
      const branchInfoRow = [
        branchInfo.code,
        branchInfo.name,
        branchInfo.location,
        branchInfo.latitude,
        branchInfo.longitude,
      ];

      doc.autoTable({
        head: [branchInfoHeaders],
        body: [branchInfoRow],
        startY: doc.autoTable.previous.finalY,
        styles: {
          font: "THSarabunNew",
          fontSize: 12,
          halign: "center",
          valign: "middle",
          textColor: [0, 0, 0],
        },
        headStyles: {
          fillColor: [221, 230, 254],
          textColor: [0, 0, 0],
          fontStyle: "bold",
        },
      });
    }

    // Branch Details Table
    const headers = [
      "Branch Code",
      "Branch Name",
      "Status",
      "Users Count",
      "Main Poles Count",
      "Secondary Poles Count",
    ];

    const rows = [
      [
        branchInfo.code,
        branchInfo.name,
        branchDetails.branchStatus ? "เปิดใช้งาน" : "ปิดใช้งาน",
        branchDetails.usersCount,
        branchDetails.mainPolesCount,
        branchDetails.secondaryPolesCount,
      ],
    ];

    doc.autoTable({
      head: [headers],
      body: rows,
      startY: doc.autoTable.previous.finalY,
      styles: {
        font: "THSarabunNew",
        fontSize: 10,
        halign: "center",
        valign: "middle",
        textColor: [0, 0, 0],
        cellPadding: 2,
      },
      headStyles: {
        fillColor: [221, 230, 254],
        textColor: [0, 0, 0],
        fontStyle: "bold",
      },
      columnStyles: {
        0: { cellWidth: "auto" },
      },
      alternateRowStyles: {
        fillColor: [248, 248, 248],
      },
    });
  };

  const generateBranchDetailReport = (worksheet, data) => {
    const { branchInfo, branchDetails } = data;

    worksheet.addRow(["Branch Detail Report"]);
    if (branchInfo) {
      worksheet.addRow(["Branch Name", branchInfo.name]);
      worksheet.addRow(["Location", branchInfo.location]);
      worksheet.addRow(["Latitude", branchInfo.latitude]);
      worksheet.addRow(["Longitude", branchInfo.longitude]);
      worksheet.addRow(["Branch ID", branchInfo.id]);
    }

    worksheet.addRow(["Users"]);
    const userHeaders = ["No.", "User Name", "Email", "Phone Number", "Status"];
    createHeaderRow(worksheet, userHeaders);

    branchDetails.users.forEach((user, index) => {
      const statusText = user.active ? "เปิดใช้งาน" : "ปิดใช้งาน";
      const dataRow = worksheet.addRow([
        index + 1,
        user.name,
        user.email,
        user.phone_no,
        statusText,
      ]);
      dataRow.eachCell((cell) => {
        cell.alignment = { vertical: "middle", horizontal: "left" };
      });

      // กำหนดสีให้กับคำว่า "เปิดใช้งาน" หรือ "ปิดใช้งาน"
      const statusCell = dataRow.getCell(5); // เซลล์ที่ 5 คือคอลัมน์สถานะ
      statusCell.value = {
        richText: [
          {
            text: statusText,
            font: {
              color: { argb: user.active ? "FF008000" : "FFFF0000" },
            },
          },
        ],
      };
    });

    worksheet.addRow(["Poles"]);
    const poleHeaders = [
      "No.",
      "Pole Name",
      "Type",
      "Location",
      "Latitude",
      "Longitude",
      "Status",
    ];
    createHeaderRow(worksheet, poleHeaders);

    branchDetails.poles.forEach((pole, index) => {
      const statusText = pole.active ? "เปิดใช้งาน" : "ปิดใช้งาน";
      const dataRow = worksheet.addRow([
        index + 1,
        pole.name,
        pole.is_master ? "เสาหลัก" : "เสารอง",
        pole.location,
        pole.lat,
        pole.lng,
        statusText,
      ]);
      dataRow.eachCell((cell) => {
        cell.alignment = { vertical: "middle", horizontal: "left" };
      });

      // กำหนดสีให้กับคำว่า "เปิดใช้งาน" หรือ "ปิดใช้งาน"
      const statusCell = dataRow.getCell(7); // เซลล์ที่ 7 คือคอลัมน์สถานะ
      statusCell.value = {
        richText: [
          {
            text: statusText,
            font: {
              color: { argb: pole.active ? "FF008000" : "FFFF0000" },
            },
          },
        ],
      };
    });
  };

  const generateBranchDetailReportPDF = (doc, data) => {
    const { branchInfo, branchDetails } = data;

    // Header for Branch Detail Report
    doc.autoTable({
      head: [["Branch Detail Report"]],
      body: [],
      styles: {
        font: "THSarabunNew",
        fontSize: 16,
        halign: "center",
        valign: "middle",
        fillColor: [131, 174, 240],
        textColor: [0, 0, 0],
        fontStyle: "bold",
      },
      startY: 20,
      tableWidth: "auto",
    });

    let currentY = doc.autoTable.previous.finalY;

    // Branch Info Table
    if (branchInfo) {
      const branchInfoHeaders = [
        "Branch Name",
        "Location",
        "Latitude",
        "Longitude",
        "Branch ID",
      ];
      const branchInfoRow = [
        branchInfo.name,
        branchInfo.location,
        branchInfo.latitude,
        branchInfo.longitude,
        branchInfo.id,
      ];

      doc.autoTable({
        head: [branchInfoHeaders],
        body: [branchInfoRow],
        startY: currentY,
        styles: {
          font: "THSarabunNew",
          fontSize: 12,
          halign: "center",
          valign: "middle",
          textColor: [0, 0, 0],
        },
        headStyles: {
          fillColor: [221, 230, 254],
          textColor: [0, 0, 0],
          fontStyle: "bold",
        },
      });

      currentY = doc.autoTable.previous.finalY;
    }

    // Header for Users Section
    doc.autoTable({
      head: [["Users"]],
      body: [],
      styles: {
        font: "THSarabunNew",
        fontSize: 16,
        halign: "left",
        valign: "middle",
        fillColor: [131, 174, 240],
        textColor: [0, 0, 0],
        fontStyle: "bold",
      },
      startY: currentY + 10, // Adding a bit of space between tables
      tableWidth: "auto",
    });

    currentY = doc.autoTable.previous.finalY;

    // User Data Table
    const userHeaders = ["No.", "User Name", "Email", "Phone Number", "Status"];
    const userRows = branchDetails.users.map((user, index) => [
      index + 1,
      user.name,
      user.email,
      user.phone_no,
      user.active ? "เปิดใช้งาน" : "ปิดใช้งาน",
    ]);

    doc.autoTable({
      head: [userHeaders],
      body: userRows,
      startY: currentY,
      styles: {
        font: "THSarabunNew",
        fontSize: 10,
        halign: "center",
        valign: "middle",
        textColor: [0, 0, 0],
        cellPadding: 2,
      },
      headStyles: {
        fillColor: [221, 230, 254],
        textColor: [0, 0, 0],
        fontStyle: "bold",
      },
      columnStyles: {
        0: { cellWidth: "auto" },
      },
      alternateRowStyles: {
        fillColor: [248, 248, 248],
      },
      didParseCell: function (data) {
        if (data.column.index === 4) {
          if (data.cell.raw === "เปิดใช้งาน") {
            data.cell.styles.textColor = [0, 128, 0]; // Green
          } else if (data.cell.raw === "ปิดใช้งาน") {
            data.cell.styles.textColor = [255, 0, 0]; // Red
          }
        }
      },
    });

    currentY = doc.autoTable.previous.finalY;

    // Header for Poles Section
    doc.autoTable({
      head: [["Poles"]],
      body: [],
      styles: {
        font: "THSarabunNew",
        fontSize: 16,
        halign: "left",
        valign: "middle",
        fillColor: [131, 174, 240],
        textColor: [0, 0, 0],
        fontStyle: "bold",
      },
      startY: currentY + 10, // Adding a bit of space between tables
      tableWidth: "auto",
    });

    currentY = doc.autoTable.previous.finalY;

    // Pole Data Table
    const poleHeaders = [
      "No.",
      "Pole Name",
      "Type",
      "Location",
      "Latitude",
      "Longitude",
      "Status",
    ];
    const poleRows = branchDetails.poles.map((pole, index) => [
      index + 1,
      pole.name,
      pole.is_master ? "เสาหลัก" : "เสารอง",
      pole.location,
      pole.lat,
      pole.lng,
      pole.active ? "เปิดใช้งาน" : "ปิดใช้งาน",
    ]);

    doc.autoTable({
      head: [poleHeaders],
      body: poleRows,
      startY: currentY,
      styles: {
        font: "THSarabunNew",
        fontSize: 10,
        halign: "center",
        valign: "middle",
        textColor: [0, 0, 0],
        cellPadding: 2,
      },
      headStyles: {
        fillColor: [221, 230, 254],
        textColor: [0, 0, 0],
        fontStyle: "bold",
      },
      columnStyles: {
        0: { cellWidth: "auto" },
      },
      alternateRowStyles: {
        fillColor: [248, 248, 248],
      },
      didParseCell: function (data) {
        if (data.column.index === 6) {
          if (data.cell.raw === "เปิดใช้งาน") {
            data.cell.styles.textColor = [0, 128, 0]; // Green
          } else if (data.cell.raw === "ปิดใช้งาน") {
            data.cell.styles.textColor = [255, 0, 0]; // Red
          }
        }
      },
    });
  };

  const generateCSV = (data) => {
    const csvRows = [];
  
    if (typeof data !== 'object' || data === null) {
      throw new Error("Invalid data structure");
    }
  
    const { poleInfo, reportInfo, sensorData, schedules } = data;
  
    // Add poleInfo
    if (poleInfo && typeof poleInfo === 'object') {
      csvRows.push("Report");
      Object.entries(poleInfo).forEach(([key, value]) => {
        csvRows.push(`${key},${value}`);
      });
      csvRows.push("");
    }
  
    // Add reportInfo
    if (reportInfo && typeof reportInfo === 'object') {
      csvRows.push("Report Information");
      Object.entries(reportInfo).forEach(([key, value]) => {
        csvRows.push(`${key},${value}`);
      });
      csvRows.push("");
    }
  
    // Add sensorData
    if (Array.isArray(sensorData) && sensorData.length > 0) {
      csvRows.push("Sensor Data");
      const headers = Object.keys(sensorData[0]);
      csvRows.push(headers.join(','));
  
      for (const row of sensorData) {
        const values = headers.map(header => {
          const escaped = ('' + row[header]).replace(/"/g, '\\"');
          return `"${escaped}"`;
        });
        csvRows.push(values.join(','));
      }
      csvRows.push(""); // Add an empty row for separation
    }
  
    // Add schedules
    if (Array.isArray(schedules) && schedules.length > 0) {
      csvRows.push("Schedules");
      const headers = Object.keys(schedules[0]);
      csvRows.push(headers.join(','));
  
      for (const row of schedules) {
        const values = headers.map(header => {
          const escaped = ('' + row[header]).replace(/"/g, '\\"');
          return `"${escaped}"`;
        });
        csvRows.push(values.join(','));
      }
    }
  
    if (csvRows.length === 0) {
      throw new Error("No data to export");
    }
  
    return csvRows.join('\n');
  };
  
  const generateAndDownloadCSV = () => {
    try {
      if (!jsonData || typeof jsonData !== 'object') {
        throw new Error("Invalid JSON data");
      }
      const csvData = generateCSV(jsonData);
      const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
      saveAs(blob, `${fileName}.csv`);
    } catch (error) {
      console.error("Error generating or downloading CSV file:", error);
      alert("เกิดข้อผิดพลาดในการสร้างไฟล์ CSV: " + error.message);
    }
  };
  

 return ReactDOM.createPortal(
    <div className="modal-background-fixedmodel">
      <div className="download-report-window">
        {loading ? (
          <div className="lds-spinner">
            <div></div>
            <div></div>
            <div></div>
            <div></div>
            <div></div>
            <div></div>
            <div></div>
            <div></div>
          </div>
        ) : (
          <>
            <h2>ดาวน์โหลดรายงาน</h2>
            {errorMessage && <div className="error-message">{errorMessage}</div>}
            <div
              className="dropdownUser"
              style={{
                border: `2px solid ${
                  selectedFormat
                    ? "#2762f8"
                    : formatDropdownOpen
                    ? "#2762f8"
                    : "#ccc"
                }`,
              }}
              onClick={toggleFormatDropdown}
            >
              {selectedFormat ? selectedFormat : "เลือก Format ไฟล์"}
              <img
                className={`arrow-icon-branch ${
                  formatDropdownOpen ? "rotate" : ""
                }`}
                src={ArrowDown}
                alt="Arrow"
              />

              {formatDropdownOpen && (
                <div
                  className="dropdown-content-adduser"
                  style={{
                    border: `1px solid ${
                      selectedFormat
                        ? "#2762f8"
                        : formatDropdownOpen
                        ? "#2762f8"
                        : "#ccc"
                    }`,
                  }}
                >
                  <ul>
                    {[".xlsx", ".pdf", ".csv"].map((format, index) => (
                      <li
                        key={index}
                        onClick={() => handleFormatSelect(format)}
                      >
                        {format}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            <div className="button-adduser">
              <button className="cancel-button" onClick={onClose}>
                ยกเลิก
              </button>
              <button className="confirm-button" onClick={handleDownloadClick}>
                ดาวน์โหลด
              </button>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body
  );
}

export default DownloadReportWindow;
